import React, { useState } from 'react';
import {
  GripDotsVerticalIcon,
  Circle1Icon,
  Circle2Icon,
  Circle3Icon,
  Circle4Icon,
  Circle5Icon,
  Circle6Icon,
  Circle7Icon,
  Circle8Icon,
  Circle9Icon,
  XmarkIcon,
  CircleExclamationIcon,
} from '@revfluence/fresh-icons/solid/esm';

import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
  DraggableProvidedDragHandleProps,
} from 'react-beautiful-dnd';
import { Skeleton } from '@frontend/shadcn/components/ui/skeleton';
import { useUpdateCreatorTasksConfig } from '@frontend/app/hooks/useUpdateCreatorTasksConfig';
import { H3 } from '@frontend/shadcn/components/typography/h3';
import { Tag } from '@frontend/app/refresh-components';
import { toast } from '@frontend/shadcn/hooks/use-toast';
import { logger } from '@common';
import { GET_CREATOR_TASKS_CONFIG } from '@frontend/app/queries/CreatorTasksConfig';
import { SectionCard } from '../components/SectionCard';
import { Button } from '@/shadcn/components/ui/button';
import { cn } from '@/shadcn/lib/utils';
import { getNewStageIconsFilled } from '../../constants';
import { CreatorTaskConfig, useCampaign } from '../CampaignContext';
import { StageToCreatorInteractionMap } from '../components/TaskItem';

const NumberIcons = [
  Circle1Icon,
  Circle2Icon,
  Circle3Icon,
  Circle4Icon,
  Circle5Icon,
  Circle6Icon,
  Circle7Icon,
  Circle8Icon,
  Circle9Icon,
];

const TaskItem = ({ task, dragHandleProps, handleRemoveTaskFromInitialTasks }: { task: CreatorTaskConfig; dragHandleProps: DraggableProvidedDragHandleProps; handleRemoveTaskFromInitialTasks: (task: CreatorTaskConfig) => void }) => {
  const title = StageToCreatorInteractionMap[task.specURI];
  const stageIcon = getNewStageIconsFilled('h-6 w-6')[task.specURI];

  return (
    <div className="flex items-center gap-2 w-full">
      <div {...dragHandleProps} className="flex items-center justify-center">
        <GripDotsVerticalIcon className="text-grey-3" fontSize={16} />
      </div>
      <div className="flex items-center gap-2 flex-1">
        {stageIcon}
        <span className="font-medium">{title}</span>
      </div>
      {task.isInitial && (
        <Button variant="ghost" size="xs" onClick={() => handleRemoveTaskFromInitialTasks(task)}>
          <XmarkIcon />
        </Button>
      )}
    </div>
  );
};

interface Props {
  projectId: number;
  initTasks: () => void;
  isCreatorTasksConfigLoading: boolean;
}

export const CreatorInteractionSetup: React.FC<Props> = ({ projectId, initTasks, isCreatorTasksConfigLoading }) => {
  const {
    initialTasks,
    remainingTasks,
    reorderTasks,
    removeTaskFromInitialTasks,
  } = useCampaign();

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const { updateCreatorTasksConfig, isUpdateCreatorTasksConfigLoading } = useUpdateCreatorTasksConfig();

  const onDragEnd = (result: DropResult) => {
    const { source, destination } = result;

    if (!destination) {
      return;
    }

    setHasUnsavedChanges(true);

    reorderTasks(source, destination);
  };

  const handleSave = () => {
    const allTasks = [...(initialTasks || []), ...(remainingTasks || [])];
    updateCreatorTasksConfig({
      variables: {
        programId: projectId,
        tasks: allTasks.map((task, index) => ({
          id: task.id,
          specURI: task.specURI,
          isInitial: task.isInitial,
          order: index + 1,
          programId: projectId,
        })),
      },
      onCompleted: () => {
        setHasUnsavedChanges(false);
        toast({
          title: 'Creator Tasks Updated',
          description: 'Creator Tasks have been updated successfully.',
          variant: 'success',
        });
      },
      onError: (error) => {
        logger.error('Failed to update Creator Tasks', error);
        toast({
          title: 'Error',
          description: error.message || 'Failed to update Creator Tasks.',
          variant: 'error',
        });
      },
      refetchQueries: [GET_CREATOR_TASKS_CONFIG],
    });
  };

  const handleCancel = () => {
    initTasks();
    setHasUnsavedChanges(false);
  };

  const handleRemoveTaskFromInitialTasks = (task: CreatorTaskConfig) => {
    removeTaskFromInitialTasks(task);
    setHasUnsavedChanges(true);
  };

  const DropNumberIcon = NumberIcons[initialTasks?.length || 0];

  return (
    <div className="flex justify-between">
      <div className="flex flex-col gap-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-1">
            <H3 className="mb-0 font-medium">
              Creator Interaction
            </H3>
            {hasUnsavedChanges && (
              <Tag
                label={(
                  <div className="flex items-center gap-1 text-yellow-5">
                    <CircleExclamationIcon className="text-yellow-5" />
                    Unsaved Changes
                  </div>
                )}
                textColor="text-yellow-5"
              />
            )}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleCancel}
              disabled={!hasUnsavedChanges || isUpdateCreatorTasksConfigLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={!hasUnsavedChanges}
              loading={isUpdateCreatorTasksConfigLoading}
            >
              Save
            </Button>
          </div>
        </div>
        <DragDropContext onDragEnd={onDragEnd}>
          <SectionCard
            title="Creator Tasks"
            description="Define how creators access their workflow tasks in the Creator Portal."
          >
            <div className="flex flex-col gap-4 self-stretch">
              <div className="self-stretch px-4 py-2 bg-grey-1 rounded-2xl flex flex-col justify-start items-start gap-2">
                <div className="self-stretch flex flex-col justify-start items-start">
                  <div className="text-sm font-medium leading-normal">Initial Tasks</div>
                  <div className="self-stretch text-primaryMuted text-sm font-normal leading-normal">
                    Require creators to complete Initial Tasks in the order set below to unlock access to all remaining
                    tasks.
                  </div>
                </div>
                <Droppable droppableId="initialTasks">
                  {(provided) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className="self-stretch flex flex-col gap-2"
                    >
                      {isCreatorTasksConfigLoading && (
                        <div className="flex flex-col items-center gap-2">
                          <Skeleton className="self-stretch h-12" />
                          <Skeleton className="self-stretch h-12" />
                        </div>
                      )}
                      {!isCreatorTasksConfigLoading &&
                        initialTasks?.map((task, index) => (
                          <Draggable key={task.specURI} draggableId={task.specURI} index={index}>
                            {(provided, snapshot) => {
                              const Icon = NumberIcons[index];
                              return (
                                <div className="flex items-center gap-2">
                                  <Icon className="text-grey-4" fontSize={16} />
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    className={cn(
                                      'px-3 border border-border rounded-2xl h-12 max-h-12 flex items-center flex-1',
                                      snapshot.isDragging && 'opacity-50',
                                      'hover:border-primary transition-all',
                                    )}
                                  >
                                    <TaskItem task={task} dragHandleProps={provided.dragHandleProps} handleRemoveTaskFromInitialTasks={handleRemoveTaskFromInitialTasks} />
                                  </div>
                                </div>
                              );
                            }}
                          </Draggable>
                        ))}
                      {provided.placeholder}
                      <div className="flex items-center gap-2">
                        <DropNumberIcon className="text-grey-4" fontSize={16} />
                        <div className="self-stretch h-12 px-2 py-3 rounded-2xl border border-dashed border-border flex justify-center items-center gap-2 flex-1">
                          Drag and drop tasks here to set as Initial Tasks.
                        </div>
                      </div>
                    </div>
                  )}
                </Droppable>
              </div>

              <div className="self-stretch flex flex-col justify-start items-start gap-2">
                <Droppable droppableId="remainingTasks">
                  {(provided) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className="self-stretch flex flex-col gap-2"
                    >
                      {isCreatorTasksConfigLoading && (
                        <div className="flex flex-col items-center gap-2">
                          <Skeleton className="self-stretch h-12" />
                          <Skeleton className="self-stretch h-12" />
                          <Skeleton className="self-stretch h-12" />
                        </div>
                      )}
                      {!isCreatorTasksConfigLoading &&
                        remainingTasks?.map((task, index) => (
                          <Draggable key={task.specURI} draggableId={task.specURI} index={index}>
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                className={cn(
                                  'px-3 border border-border rounded-2xl h-12 max-h-12 flex items-center',
                                  snapshot.isDragging && 'opacity-50',
                                  'hover:border-primary transition-all',
                                )}
                              >
                                <TaskItem task={task} dragHandleProps={provided.dragHandleProps} handleRemoveTaskFromInitialTasks={handleRemoveTaskFromInitialTasks} />
                              </div>
                            )}
                          </Draggable>
                        ))}
                      {provided.placeholder}
                      <div className="self-stretch h-12 px-2 py-3 rounded-2xl border border-dashed border-border flex justify-center items-center gap-2">
                        Drag and drop tasks here to remove from Initial Tasks.
                      </div>
                    </div>
                  )}
                </Droppable>
              </div>
            </div>
          </SectionCard>
        </DragDropContext>
      </div>
    </div>
  );
};
